"use client"

import { useState, useEffect } from "react"
import { DollarSign, Pencil, Plus, Trash2, Save, X } from "lucide-react"
import config from "../config"
import axios from "axios"
import { useAuth } from "../context/AuthContext"
import { useSuccessFailure } from "../context/SuccessFailureContext"
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes } from 'react-icons/fa';


// Add axios interceptor for authentication
const publicEndpoints = ['/auth/', '/events/all/'];

axios.interceptors.request.use(
  (config) => {
    // Skip adding auth header for public endpoints
    const isPublicEndpoint = publicEndpoints.some(endpoint =>
      config.url.endsWith(endpoint)
    );

    if (!isPublicEndpoint) {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const BudgetTracker = ({ event }) => {
  const { user, isLoggedIn } = useAuth()
  const { showSuccessModal, showFailureModal } = useSuccessFailure()

  // State variables
  const [expenses, setExpenses] = useState([])
  const [categories, setCategories] = useState([])
  const [editExpenseState, setEditExpenseState] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Expense form state
  const [newExpense, setNewExpense] = useState({
    description: "",
    amount: "",
    category: null,
    date: new Date().toISOString().split('T')[0],  // Current date in YYYY-MM-DD format
    status: "planned",
    notes: ""
  })
  const [editingExpense, setEditingExpense] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [expensesPerPage] = useState(10);

  // Service request and expense form state
  const [showServiceRequest, setShowServiceRequest] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);

  // Calculate total expenses
  const totalExpenses = expenses.reduce((total, expense) => {
    const amount = parseFloat(expense.amount) || 0;
    return total + amount;
  }, 0)

  // Fetch categories from backend
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/events/budget/categories/`)
      setCategories(response.data)
    } catch (err) {
      // console.error("Error fetching categories:", err)
      setError("Failed to fetch budget categories")
    }
  }

  // API Functions
  const fetchExpenses = async () => {
    try {
      if (!event?.slug) {
        // console.log("No event slug available")
        return
      }

      setLoading(true)
      const response = await axios.get(`${config.API_BASE_URL}/events/${event.slug}/expenses/`, {
        headers: {
          Authorization: `Bearer ${user?.access_token}`
        }
      })
      setExpenses(response.data)
    } catch (err) {
      // console.error("Error fetching expenses:", err)
      setError(err.response?.data?.detail || "Failed to fetch expenses")
      showFailureModal(err.response?.data?.detail || "Failed to fetch expenses")
    } finally {
      setLoading(false)
    }
  }

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (editingExpense) {
      setEditExpenseState(prev => ({
        ...prev,
        [name]: value
      }))
    } else {
      setNewExpense(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleCategoryChange = (e) => {
    const selectedCategory = categories.find(cat => cat.id === parseInt(e.target.value))
    setNewExpense(prev => ({
      ...prev,
      category: selectedCategory
    }))
  }

  // Expense handlers
  const handleEditExpense = (expense) => {
    // Create a copy of the expense with initial state
    const initialState = {
      description: expense.description || "",
      amount: expense.amount || "",
      category: expense.category,
      date: expense.date || new Date().toISOString().split('T')[0],
      status: expense.status || "planned",
      notes: expense.notes || ""
    }
    setEditExpenseState(initialState)
    setEditingExpense(expense)
  }

  const handleSaveEdit = async () => {
    try {
      if (!editExpenseState || !editingExpense) return

      const { id } = editingExpense
      const updatedExpense = {
        description: editExpenseState.description,
        amount: parseFloat(editExpenseState.amount),
        category: editExpenseState.category,
        date: editExpenseState.date,
        status: editExpenseState.status,
        notes: editExpenseState.notes
      }

      await axios.put(`${config.API_BASE_URL}/events/${event.slug}/expenses/${id}/`, updatedExpense)
      await fetchExpenses()  // Ensure data is fetched after update
      setEditExpenseState(null)
      setEditingExpense(null)
      showSuccessModal("Expense updated successfully")
    } catch (err) {
      // console.error("Error updating expense:", err)
      setError(err.response?.data?.detail || "Failed to update expense")
      showFailureModal(err.response?.data?.detail || "Failed to update expense")
    }
  }

  const handleCancelEdit = () => {
    setEditExpenseState(null)
    setEditingExpense(null)
  }

  const handleDeleteExpense = async (expense) => {
    try {
      const { id } = expense
      if (!window.confirm('Are you sure you want to delete this expense? This action cannot be undone.')) return

      await axios.delete(`${config.API_BASE_URL}/events/${event.slug}/expenses/${id}/`)
      fetchExpenses()
      showSuccessModal("Expense deleted successfully")
    } catch (err) {
      // console.error("Error deleting expense:", err)
      setError(err.response?.data?.detail || "Failed to delete expense")
      showFailureModal(err.response?.data?.detail || "Failed to delete expense")
    }
  }

  const handleSubmitExpense = async (e) => {
    e.preventDefault()
    try {
      // Validate required fields
      if (!event?.slug) {
        setError("Please select an event first")
        return
      }
      if (!newExpense.description.trim()) {
        setError("Description is required")
        return
      }
      if (!newExpense.amount || parseFloat(newExpense.amount) <= 0) {
        setError("Please enter a valid amount greater than 0")
        return
      }
      if (!newExpense.category) {
        setError("Please select a category")
        return
      }
      if (!newExpense.date) {
        setError("Please select a date")
        return
      }

      // Format the data to match backend expectations
      const expenseData = {
        event: event.slug,
        category: newExpense.category.id,
        description: newExpense.description.trim(),
        amount: parseFloat(newExpense.amount),
        date: newExpense.date,
        status: "planned",
        notes: newExpense.notes.trim() || ""
      }

      // Show loading state
      setLoading(true)
      setError(null)

      const response = await axios.post(
        `${config.API_BASE_URL}/events/${event.slug}/expenses/create/`,
        expenseData,
        {
          headers: {
            Authorization: `Bearer ${user?.access_token}`
          }
        }
      )

      // Reset form
      setNewExpense({
        description: "",
        amount: "",
        category: null,
        date: new Date().toISOString().split('T')[0],
        status: "planned",
        notes: ""
      })

      // Refresh expenses
      fetchExpenses()

      // Show success message
      showSuccessModal("Expense added successfully")
    } catch (err) {
      // console.error("Error creating expense:", err)
      setError(err.response?.data?.detail || "Failed to create expense")
    } finally {
      setLoading(false)
    }
  }

  // Expense handlers
  const handleUpdateExpense = async (expense) => {
    try {
      const { id, status } = expense
      await axios.put(`${config.API_BASE_URL}/events/${event.slug}/expenses/${id}/`, {
        status
      })
      fetchExpenses()
      showSuccessModal("Expense updated successfully")
    } catch (err) {
      // console.error("Error updating expense:", err)
      setError(err.response?.data?.detail || "Failed to update expense")
      showFailureModal(err.response?.data?.detail || "Failed to update expense")
    }
  }

  // Effect hooks
  useEffect(() => {
    if (event?.slug) {
      fetchCategories()
      fetchExpenses()
    }
  }, [event?.slug])

  // Render
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        {error}
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Add New Expense Form */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Add New Expense</h3>
        <form onSubmit={handleSubmitExpense} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expense Description</label>
            <input
              type="text"
              name="description"
              value={newExpense.description}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder="Enter expense description"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
            <input
              type="number"
              name="amount"
              value={newExpense.amount}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              min="0"
              step="0.01"
              placeholder="Enter amount"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              name="category"
              value={newExpense.category?.id || ''}
              onChange={handleCategoryChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              name="date"
              value={newExpense.date}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              name="notes"
              value={newExpense.notes}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
              placeholder="Add any additional notes..."
            />
          </div>
          <button
            type="submit"
            disabled={!newExpense.description || !newExpense.amount || !newExpense.category}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4 inline-block mr-2" />
            Add Expense
          </button>
        </form>
      </div>

      {/* Expense List */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold">Budget Tracker</h2>
          <div className="flex gap-4">
            <button
              onClick={() => setShowExpenseForm(true)}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              <FaPlus className="inline mr-2" /> Add Expense
            </button>
            <button
              onClick={() => setShowServiceRequest(true)}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              <FaPlus className="inline mr-2" /> Request Service
            </button>
          </div>
        </div>
        <div className="mb-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Total Expenses</h3>
            <p className="text-2xl font-bold text-blue-600">KES {totalExpenses.toLocaleString()}</p>
          </div>
        </div>
        <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 sm:px-6 lg:px-8">
            <div className="overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {expenses
                    .slice((currentPage - 1) * expensesPerPage, currentPage * expensesPerPage)
                    .map((expense) => (
                      <tr key={expense.id}>
                        <td className="px-6 py-4">
                          {editingExpense?.id === expense.id ? (
                            <input
                              name="description"
                              type="text"
                              value={editExpenseState?.description || ''}
                              onChange={handleInputChange}
                              className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                              placeholder="Description"
                              style={{ minWidth: '200px' }}
                            />
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900">{expense.description}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {editingExpense?.id === expense.id ? (
                            <input
                              name="amount"
                              type="number"
                              value={editExpenseState?.amount || ''}
                              onChange={handleInputChange}
                              className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                              placeholder="Amount"
                            />
                          ) : `KES ${expense.amount.toLocaleString()}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {editingExpense?.id === expense.id ? (
                            <select
                              name="category"
                              value={editExpenseState?.category || ''}
                              onChange={handleInputChange}
                              className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="" disabled>Select category</option>
                              {categories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium">{categories.find(c => c.id === expense.category)?.name || 'Unknown'}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {editingExpense?.id === expense.id ? (
                            <input
                              name="date"
                              type="date"
                              value={editExpenseState?.date || ''}
                              onChange={handleInputChange}
                              className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                            />
                          ) : new Date(expense.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {editingExpense?.id === expense.id ? (
                            <select
                              name="status"
                              value={editExpenseState?.status || ''}
                              onChange={handleInputChange}
                              className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="planned">Planned</option>
                              <option value="approved">Approved</option>
                              <option value="paid">Paid</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          ) : (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${expense.status === 'planned' ? 'bg-blue-100 text-blue-800' :
                              expense.status === 'approved' ? 'bg-green-100 text-green-800' :
                                expense.status === 'paid' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                              }`}>
                              {expense.status.charAt(0).toUpperCase() + expense.status.slice(1)}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {editingExpense?.id === expense.id ? (
                              <>
                                <button
                                  onClick={handleSaveEdit}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                                  title="Save"
                                >
                                  <Save className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-white bg-gray-600 hover:bg-gray-700"
                                  title="Cancel"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditExpense(expense)}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                                  title="Edit"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteExpense(expense)}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-white bg-red-600 hover:bg-red-700"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {expenses.length > expensesPerPage && (
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 border rounded mr-2 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={currentPage >= Math.ceil(expenses.length / expensesPerPage)}
                    className="px-3 py-1.5 border rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BudgetTracker
